import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employee/dashboard`, {
        headers: getAuthHeader()
      });
      setDashboard(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching dashboard:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, getAuthHeader]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employee/announcements`, {
        headers: getAuthHeader()
      });
      setAnnouncements(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching announcements:', error);
      }
    }
  }, [BACKEND_URL, getAuthHeader]);

  useEffect(() => {
    fetchDashboard();
    fetchAnnouncements();
  }, [fetchDashboard, fetchAnnouncements]);

  if (loading) return <div>Loading dashboard...</div>;
  if (!dashboard) return <div>Error loading dashboard</div>;

  const { employee, leaves, payslips, leave_balance } = dashboard;

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card className="bg-gradient-to-r from-primary to-secondary text-white">
        <CardContent className="p-6">
          <h1 className="text-3xl font-bold mb-2">Welcome, {employee.full_name}!</h1>
          <p className="text-white/90">Employee ID: {employee.employee_id} • {employee.designation}</p>
          <p className="text-white/80 text-sm mt-1">{employee.department}</p>
        </CardContent>
      </Card>

      {/* Leave Balance */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Casual Leave</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{leave_balance.casual || 0}</div>
            <p className="text-xs text-gray-500 mt-1">days available</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Sick Leave</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{leave_balance.sick || 0}</div>
            <p className="text-xs text-gray-500 mt-1">days available</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Earned Leave</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{leave_balance.earned || 0}</div>
            <p className="text-xs text-gray-500 mt-1">days available</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Leave Requests */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Leave Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {leaves && leaves.length > 0 ? (
            <div className="space-y-3">
              {leaves.map((leave) => (
                <div key={leave.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} Leave</span>
                      <Badge className={getStatusBadge(leave.status)}>
                        {leave.status.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      {new Date(leave.from_date).toLocaleDateString()} - {new Date(leave.to_date).toLocaleDateString()} ({leave.days} days)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{leave.reason}</div>
                  </div>
                  {leave.status === 'pending' ? (
                    <AlertCircle className="text-yellow-500" size={20} />
                  ) : leave.status === 'approved' ? (
                    <CheckCircle className="text-green-500" size={20} />
                  ) : (
                    <XCircle className="text-red-500" size={20} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No leave requests yet</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Payslips */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Payslips</CardTitle>
        </CardHeader>
        <CardContent>
          {payslips && payslips.length > 0 ? (
            <div className="space-y-2">
              {payslips.map((payslip) => (
                <div key={payslip.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{payslip.month}</div>
                    <div className="text-sm text-gray-600">Net Salary: ₹{payslip.net_salary.toLocaleString()}</div>
                  </div>
                  <Button size="sm" variant="outline">View</Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No payslips available</p>
          )}
        </CardContent>
      </Card>

      {/* Company Announcements */}
      {announcements && announcements.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="text-blue-600" size={20} />
              Company Announcements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {announcements.slice(0, 3).map((announcement) => (
                <div key={announcement.id} className="p-3 bg-white border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold">{announcement.title}</h4>
                    <Badge className={
                      announcement.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      announcement.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      'bg-blue-100 text-blue-800'
                    }>
                      {announcement.priority.toUpperCase()}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700">{announcement.content}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeeDashboard;
